import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent
} from "react";
import { useNavigate } from "react-router-dom";
import { HugeiconsIcon } from "@hugeicons/react";
import { Search01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { useAuthContext } from "providers/Auth";
import { IconButton, Modal } from "shared/components";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { cx } from "shared/utils";
import type { MasterSearchItem } from "api/masterSearch";
import { useMasterSearch } from "./useMasterSearch";
import { buildDetailRoute, buildQuickLinks, routableTypes } from "./routes";
import { MasterSearchResults } from "./MasterSearchResults";
import { flattenGroupItems } from "./utils";
import {
  closePalette,
  isPaletteOpen,
  openPalette,
  usePaletteOwner
} from "./store";
import {
  ENTITY_TYPE_LABELS,
  MASTER_SEARCH_PLACEHOLDER,
  MIN_QUERY_LENGTH
} from "./constants";

const railTriggerClassName = cx(
  "flex w-full items-center gap-4 overflow-hidden whitespace-nowrap rounded-lg p-3",
  "text-sm font-medium text-content-500 transition-all hover:bg-alpha-40"
);

const kbdClassName =
  "rounded border border-alpha-100 bg-white px-1 font-sans text-[10px] text-content-200";

const isApplePlatform = () =>
  typeof navigator !== "undefined" &&
  /mac|iphone|ipad/i.test(navigator.userAgent);

const ShortcutHint = () => (
  <kbd className="rounded border border-alpha-100 bg-alpha-40 px-1.5 py-0.5 font-sans text-[10px] font-medium text-content-200">
    {isApplePlatform() ? "⌘K" : "Ctrl K"}
  </kbd>
);

interface MasterSearchProps {
  variant?: "rail" | "icon";
}

export const MasterSearch = ({ variant = "rail" }: MasterSearchProps) => {
  const navigate = useNavigate();
  const { type } = useAuthContext();
  const instanceId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const open = usePaletteOwner(instanceId);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [debouncedQuery] = useDebouncedValue(query, 300);
  const trimmedQuery = debouncedQuery.trim();
  // The debounce has caught up with what the user sees. Until it has, showing
  // (or Enter-opening) the previous query's cached results would be wrong.
  const settled = query.trim() === trimmedQuery;

  const { data, isFetching, isError } = useMasterSearch(trimmedQuery, open);
  const allowedTypes = useMemo(() => routableTypes(type), [type]);
  // Partners with sites_and_communities call these "Communities" / "Sites".
  const dict = useUnitsPropertiesDictionary();
  const typeLabels = useMemo(
    () => ({
      ...ENTITY_TYPE_LABELS,
      property: dict.Properties,
      listing: dict.Units
    }),
    [dict.Properties, dict.Units]
  );
  const groups = useMemo(() => {
    if (!settled || data?.query !== trimmedQuery) return undefined;
    return data.groups.filter((group) => allowedTypes.includes(group.type));
  }, [data, settled, trimmedQuery, allowedTypes]);
  const flatItems = useMemo(() => flattenGroupItems(groups ?? []), [groups]);

  const handleOpen = () => openPalette(instanceId);
  const handleClose = () => {
    closePalette();
    setQuery("");
    setActiveIndex(0);
  };

  // ⌘K / Ctrl+K from anywhere. Only the rail instance owns the shortcut; the
  // shared store means it toggles the palette wherever it is already open.
  useEffect(() => {
    if (variant !== "rail") return;

    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.repeat || event.shiftKey || event.altKey) return;
      if (!event.metaKey && !event.ctrlKey) return;
      if (event.key?.toLowerCase() !== "k") return;

      event.preventDefault();
      if (isPaletteOpen()) closePalette();
      else openPalette(instanceId);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [variant, instanceId]);

  // Focus the input on the frame the palette mounts. `autoFocus` alone waits
  // for the Modal's 300ms enter transition, so characters typed straight
  // after ⌘K were landing on <body> and getting dropped.
  useEffect(() => {
    if (!open) return;

    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open]);

  const handleSelect = (item: MasterSearchItem) => {
    const route = buildDetailRoute(item, type);
    handleClose();
    if (route) navigate(route);
  };

  const handleQuickLink = (route: string) => {
    handleClose();
    navigate(route);
  };

  const handleQueryChange = (next: string) => {
    setQuery(next);
    setActiveIndex(0);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (!flatItems.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((index) => (index + 1) % flatItems.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex(
        (index) => (index - 1 + flatItems.length) % flatItems.length
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = flatItems[activeIndex];
      if (item) handleSelect(item);
    }
  };

  return (
    <>
      {variant === "rail" ? (
        <button
          type="button"
          aria-label="Search"
          onClick={handleOpen}
          className={railTriggerClassName}
        >
          <HugeiconsIcon
            icon={Search01Icon}
            size={20}
            className="shrink-0 stroke-content-500"
            aria-hidden
          />
          <span className="hidden grow items-center justify-between overflow-hidden md:group-hover:flex">
            <span className="overflow-hidden text-ellipsis">Search</span>
            <ShortcutHint />
          </span>
        </button>
      ) : (
        <IconButton
          HugeIcon={Search01Icon}
          borderLess
          aria-label="Search"
          className="stroke-content-200"
          onClick={handleOpen}
        />
      )}

      {/* Shared Modal (headlessui): focus trap, focus restore, scroll lock and
          Escape handling that cannot leak to a dialog underneath. */}
      <Modal
        opened={open}
        onClose={handleClose}
        zIndex={60}
        backdropClassName="bg-black/40 backdrop-blur-sm"
        panelClassName="mt-[10vh] w-[800px] max-w-[calc(100vw-24px)] self-start overflow-hidden rounded-2xl border border-alpha-100 !bg-white shadow-2xl"
      >
        <div className="flex h-14 items-center gap-3 border-b border-alpha-100 px-4">
          <HugeiconsIcon
            icon={Search01Icon}
            size={20}
            className="shrink-0 stroke-content-200"
            aria-hidden
          />
          <input
            id="master-search"
            ref={inputRef}
            type="text"
            autoFocus
            autoComplete="off"
            spellCheck={false}
            aria-label="Search people and records"
            placeholder={MASTER_SEARCH_PLACEHOLDER}
            value={query}
            onChange={(e) => handleQueryChange(e.target.value)}
            onKeyDown={handleKeyDown}
            className="h-full w-full border-0 bg-transparent p-0 text-base text-content-1000 outline-none ring-0 placeholder:text-content-200 focus:border-0 focus:outline-none focus:ring-0"
          />
          <kbd className="shrink-0 rounded border border-alpha-100 bg-alpha-40 px-1.5 py-0.5 font-sans text-[10px] font-medium text-content-200">
            esc
          </kbd>
        </div>
        <MasterSearchResults
          query={settled ? trimmedQuery : ""}
          minQueryLength={MIN_QUERY_LENGTH}
          groups={groups}
          isFetching={isFetching || !settled}
          isError={isError}
          activeIndex={activeIndex}
          allowedTypes={allowedTypes}
          typeLabels={typeLabels}
          quickLinksFor={(item) => buildQuickLinks(item, type)}
          onSelect={handleSelect}
          onQuickLink={handleQuickLink}
          onHover={setActiveIndex}
        />
        <div className="flex items-center gap-4 border-t border-alpha-100 bg-alpha-20 px-4 py-2 text-[11px] text-content-200">
          <span className="flex items-center gap-1">
            <kbd className={kbdClassName}>↑↓</kbd>
            Navigate
          </span>
          <span className="flex items-center gap-1">
            <kbd className={kbdClassName}>↵</kbd>
            Open
          </span>
          <span className="flex items-center gap-1">
            <kbd className={kbdClassName}>esc</kbd>
            Close
          </span>
        </div>
      </Modal>
    </>
  );
};
